import * as React from 'react';
import PropTypes from 'prop-types';
import {
  unstable_composeClasses as composeClasses,
  unstable_useEnhancedEffect as useEnhancedEffect,
} from '@mui/utils';
import { styled } from '@mui/material/styles';
import InputBase, { InputBaseProps } from '@mui/material/InputBase';
import { GridEditInputCell, GridRenderEditCellParams } from '@mui/x-data-grid-pro';
import { getDataGridUtilityClass } from '@mui/x-data-grid-pro';
import { useGridRootProps } from '@mui/x-data-grid-pro';
import { DataGridProProps } from '@mui/x-data-grid-pro';
import { useGridApiContext } from '@mui/x-data-grid-pro';

type OwnerState = DataGridProProps;

const useUtilityClasses = (ownerState: OwnerState) => {
  const { classes } = ownerState;

  const slots = {
    root: ['editInputCell'],
  };

  return composeClasses(slots, getDataGridUtilityClass, classes);
};

const GridEditInputCellRoot = styled(InputBase, {
  name: 'MuiDataGrid',
  slot: 'EditInputCell',
  overridesResolver: (props, styles) => styles.editInputCell,
})<{ ownerState: OwnerState }>(({ theme }) => ({
  ...theme.typography.body2,
  padding: '1px 0',
  '& input': {
    padding: '0 16px',
    height: '100%',
  },
}));

export interface GridEditInputCellProps
  extends GridRenderEditCellParams,
    Omit<InputBaseProps, 'id' | 'value' | 'tabIndex' | 'ref'> {
  debounceMs?: number;
  /**
   * Callback called when the value is changed by the user.
   * @param {React.ChangeEvent<HTMLInputElement>} event The event source of the callback.
   * @param {Date | null} newValue The value that is going to be passed to `apiRef.current.setEditCellValue`.
   * @returns {Promise<void> | void} A promise to be awaited before calling `apiRef.current.setEditCellValue`
   */
  onValueChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    newValue: string,
  ) => Promise<void> | void;
}

export const CustomEditInputCell = React.forwardRef<HTMLInputElement, GridEditInputCellProps>(
  (props, ref) => {
    const rootProps = useGridRootProps();

    const {
      id,
      value,
      formattedValue,
      api,
      field,
      row,
      rowNode,
      colDef,
      cellMode,
      isEditable,
      tabIndex,
      hasFocus,
      isValidating,
      debounceMs = 200,
      isProcessingProps,
      onValueChange,
      ...other
    } = props;

    const apiRef = useGridApiContext();
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [valueState, setValueState] = React.useState(value);
    const classes = useUtilityClasses(rootProps);

    const handleChange = React.useCallback(
      async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;

        if (onValueChange) {
          await onValueChange(event, newValue);
        }

        const column = apiRef.current.getColumn(field);

        setValueState(colDef.type === 'number' ? Number(newValue) : newValue);

        apiRef.current.setEditCellValue(
          { id, field, value: event.target.value, debounceMs, unstable_skipValueParser: true },
          event,
        );
      },
      [apiRef, debounceMs, field, id, onValueChange],
    );

    const meta = apiRef.current.unstable_getEditCellMeta
      ? apiRef.current.unstable_getEditCellMeta(id, field)
      : {};

    React.useEffect(() => {
      if (meta?.changeReason !== 'debouncedSetEditCellValue') {
        setValueState(value);
      }
    }, [meta?.changeReason, value]);

    useEnhancedEffect(() => {
      if (hasFocus) {
        inputRef.current!.focus();
      }
    }, [hasFocus]);

    return (
      <GridEditInputCellRoot
        ref={ref}
        inputRef={inputRef}
        className={classes.root}
        ownerState={rootProps}
        fullWidth
        type={colDef.type === 'number' ? colDef.type : 'text'}
        value={valueState ?? ''}
        onChange={handleChange}
        endAdornment={
          isProcessingProps ? (
            <rootProps.slots.loadIcon fontSize="small" color="action" />
          ) : undefined
        }
        onFocus={(event) => {
          event.target.select();
        }}
        sx={{ 
          flex: 1,
          padding: 0,
          minWidth: 40,
          minHeight: 30,
          // Additional CSS to ensure spinners are positioned correctly and larger
          '& input[type="number"]::-webkit-outer-spin-button, & input[type="number"]::-webkit-inner-spin-button': {
              marginLeft: 'auto',
              marginRight: '0',
              height: '30px', // Increase height
              width: '20px', // Increase width
              opacity: 1, // Ensure they're visible,
          },
          '& input[type="number"]': {
              paddingRight: '0px', // Add right padding back
              minHeight: 30,
          }
      }}
        {...other}
      />
    );
  },
);

CustomEditInputCell.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "yarn proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * GridApi that let you manipulate the grid.
   */
  api: PropTypes.object,
  /**
   * The mode of the cell.
   */
  cellMode: PropTypes.oneOf(['edit', 'view']),
  changeReason: PropTypes.oneOf(['debouncedSetEditCellValue', 'setEditCellValue']),
  /**
   * The column of the row that the current cell belongs to.
   */
  colDef: PropTypes.object,
  debounceMs: PropTypes.number,
  /**
   * The column field of the cell that triggered the event.
   */
  field: PropTypes.string,
  /**
   * The cell value formatted with the column valueFormatter.
   */
  formattedValue: PropTypes.any,
  /**
   * If true, the cell is the active element.
   */
  hasFocus: PropTypes.bool,
  /**
   * The grid row id.
   */
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * If true, the cell is editable.
   */
  isEditable: PropTypes.bool,
  isProcessingProps: PropTypes.bool,
  isValidating: PropTypes.bool,
  /**
   * Callback called when the value is changed by the user.
   * @param {React.ChangeEvent<HTMLInputElement>} event The event source of the callback.
   * @param {Date | null} newValue The value that is going to be passed to `apiRef.current.setEditCellValue`.
   * @returns {Promise<void> | void} A promise to be awaited before calling `apiRef.current.setEditCellValue`
   */
  onValueChange: PropTypes.func,
  /**
   * The row model of the row that the current cell belongs to.
   */
  row: PropTypes.any,
  /**
   * The node of the row that the current cell belongs to.
   */
  rowNode: PropTypes.object,
  /**
   * the tabIndex value.
   */
  tabIndex: PropTypes.oneOf([-1, 0]),
  /**
   * The cell value.
   * If the column has `valueGetter`, use `params.row` to directly access the fields.
   */
  value: PropTypes.any,
} as any;

export { GridEditInputCell };

export const renderEditInputCell = (params: GridEditInputCellProps) => (
  <GridEditInputCell {...params} />
);